import React from 'react';
import { useTranslation } from 'react-i18next';
import bemBlock from '@gik/core/utils/bemBlock';
import { InkindSearch } from '..';
import Logo from '@gik/core/assets/img/icons/giveinkind-logo.svg';
import { Button, IMenuItem } from '@gik/ui';
import SvgIcon from '@gik/ui/SvgIcon';
import Link from '@/components/Link';
import Drawer from '@gik/ui/Drawer';
import AppNavMobile from '../AppNavMobile';
import UserAvatarMenu from '../UserAvatarMenu';
import { translationKeys } from '@gik/i18n/en/app';
import routes from '@gik/core/routes';
import MenuBar from '@gik/ui/MenuBar/MenuBar';
import NavBar from '@gik/ui/NavBar/NavBar';
import { useUserStore } from '@gik/core/store/UserStore';
import { auth } from '@gik/auth';
import { CreateClickedLocation, useCreateAnalyticsController } from '@gik/create/hooks/useCreateAnalytics';
import { useNavBarStore } from '@gik/ui/NavBar/NavBarStore';
import { isHomepage } from '@/utils/environment';

export const appHeaderBlockName = 'app-header';

const bem = bemBlock(appHeaderBlockName);

export default function AppHeader() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
  const mobileSearchRef = React.useRef<HTMLInputElement>(null);
  const userId = useUserStore(state => state.id);
  const { t } = useTranslation();

  const { trackCreateClicked } = useCreateAnalyticsController();

  const isNavBarBlurred = useNavBarStore(state => state.blurred);

  const whyGiveInkindItems: IMenuItem[] = React.useMemo(
    () => [
      { label: t(translationKeys.howItWorks), href: routes.howItWork },
      { label: t(translationKeys.features), href: routes.features },
      // { label: t(translationKeys.comparisonChart), href: routes.competitors },
      // { label: t(translationKeys.benefits), href: routes.benefits },
      { label: t(translationKeys.tipsAndSamplePages), href: routes.tipsAndSamplePages },
      { label: t(translationKeys.commonQuestions), href: routes.commonQuestions },
    ],
    []
  );

  const waysToHelpItems: IMenuItem[] = React.useMemo(
    () => [
      { label: t(translationKeys.bySituation), href: routes.giveBySituation },
      { label: t(translationKeys.byGiftType), href: routes.byGiftType },
      { label: t(translationKeys.blog), href: routes.blog },
      { label: t(translationKeys.giftGuides), href: routes.giftGuides },
    ],
    []
  );

  function handleOpenMobileNav() {
    setIsOpen(true);
  }

  function handleOpenMobileSearch() {
    handleOpenMobileNav();
    // wait for the mobile nav to open
    setTimeout(() => {
      mobileSearchRef?.current.focus();
    }, 300);
  }

  async function handleClickSignIn() {
    const isHomePage = isHomepage();
    const userId = await auth.login({
      reloadAOnLogin: !isHomePage,
    });

    if (isHomePage && userId) {
      const { UI } = await import('@gik/ui');

      const closeLoadingSpinnerOverlay = UI.loadingSpinnerOverlay();

      const { getUserPages } = await import('@gik/api/inkinds/userPages');
      const { navigateTo } = await import('@/utils/environment');

      const pages = await getUserPages(userId);

      if (pages.length === 1) {
        navigateTo(`/inkinds/${pages[0].routeId}/${pages[0].slug}`);
      } else {
        navigateTo(`/user/profile`);
      }

      closeLoadingSpinnerOverlay?.();
    }
  }

  function handleClickExpand() {
    setIsSearchExpanded(true);
  }

  function handleExpandClose() {
    setIsSearchExpanded(false);
  }

  return (
    <NavBar contained className={bem()} blurred={isNavBarBlurred}>
      <Drawer
        fixed
        className="gik-nav-mobile-drawer"
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        portal={document.body}
      >
        <AppNavMobile searchRef={mobileSearchRef} />
      </Drawer>

      <a className={bem('nav-mobile-trigger')} onClick={handleOpenMobileNav}>
        <SvgIcon name="Menu" />
      </a>

      <div className={bem('logo')}>
        <Link href={routes.home}>
          <a>
            <Logo />
          </a>
        </Link>
      </div>

      <ul className={bem('nav-mobile')}>
        <li>
          <Button circle variant="default" className={bem('btn-search')} onClick={handleOpenMobileSearch}>
            <SvgIcon name="Search" size="base" />
          </Button>
        </li>
        {!userId && (
          <li>
            <Button variant="default-link" className={bem('btn-signin')} onClick={handleClickSignIn}>
              {t(translationKeys.signin)}
            </Button>
          </li>
        )}
        {userId && (
          <li>
            <Link href={routes.userProfile}>
              <Button circle variant="secondary" className={bem('btn-home')}>
                <SvgIcon name="HomeOutline" size="base" />
              </Button>
            </Link>
          </li>
        )}
      </ul>

      <ul className={bem('nav', [{ 'search-expanded': isSearchExpanded }])}>
        <li className={bem('nav-search')}>
          {isSearchExpanded && (
            <a onClick={handleExpandClose}>
              <SvgIcon name="XOutline" />{' '}
            </a>
          )}
          <InkindSearch pill onBlur={handleExpandClose} />
          {!isSearchExpanded && (
            <Button circle variant="default" className={bem('btn-search')} onClick={handleClickExpand}>
              <SvgIcon name="Search" size="base" />
            </Button>
          )}
        </li>

        {!isSearchExpanded && (
          <MenuBar
            menuList={[
              {
                title: 'Why Give InKind?',
                appendIcon: {
                  set: 'hero-icons',
                  name: 'ChevronDown',
                },
                menu: whyGiveInkindItems,
              },
              {
                title: 'Ways to Help',
                appendIcon: {
                  set: 'hero-icons',
                  name: 'ChevronDown',
                },
                menu: waysToHelpItems,
              },
            ]}
          />
        )}

        <li onClick={() => trackCreateClicked(CreateClickedLocation.PAGE_HEADER)}>
          <Link href={routes.create}>
            <Button variant="secondary" pill className={bem('btn-start')}>
              {t(translationKeys.startAPage)}
            </Button>
          </Link>
        </li>
        {userId && (
          <li className={bem('user-avatar-container')}>
            <UserAvatarMenu />
          </li>
        )}
        {!userId && (
          <li>
            <Button className={bem('btn-signin')} variant="default-plain" onClick={handleClickSignIn}>
              {t(translationKeys.signin)}
            </Button>
          </li>
        )}
      </ul>
    </NavBar>
  );
}
